export type Pillar = {
    t: number;
    parRate: number;
};

export type CurvePoint = {
    t: number;
    discount: number;
    zeroRate: number;
};

export type ForwardRatePoint = {
    fromT: number;
    toT: number;
    forwardRate: number;
};

/**
 * Blends forward rates toward a UFR (Ultimate Forward Rate)
 * between toT = 11 and toT = 20.
 *
 * Weighting:
 *   toT=11 -> 10% UFR
 *   toT=12 -> 20% UFR
 *   ...
 *   toT=19 -> 80% UFR
 *   toT=20 -> 90% UFR
 */
export function applyUfrWeighting(forwards: ForwardRatePoint[], ufr: number): ForwardRatePoint[] {
    return forwards.map((point) => {
        const { toT, forwardRate } = point;

        // Outside blending range -> unchanged
        if (toT < 11 || toT > 20) {
            return { ...point };
        }

        // Linear weighting:
        // 11 -> 0.1
        // 20 -> 0.9
        const ufrWeight = (toT - 10) / 10;

        const weightedForwardRate = (1 - ufrWeight) * forwardRate + ufrWeight * ufr;

        return {
            ...point,
            forwardRate: weightedForwardRate
        };
    });
}

export function calculateForwardRates(curve: CurvePoint[]): ForwardRatePoint[] {
    const sorted = [...curve].sort((a, b) => a.t - b.t);
    const byT = new Map(sorted.map((p) => [p.t, p]));

    const forwards: ForwardRatePoint[] = [];
    forwards.push({
        fromT: 0,
        toT: 1,
        forwardRate: sorted[0].zeroRate
    });
    for (const point of sorted) {
        const t = point.t;

        if (t <= 1) continue;

        const previous = byT.get(t - 1);

        if (!previous) {
            throw new Error(`Missing curve point for t=${t - 1}`);
        }

        const forwardRate = Math.pow(1 + point.zeroRate, t) / Math.pow(1 + previous.zeroRate, t - 1) - 1;

        forwards.push({
            fromT: t - 1,
            toT: t,
            forwardRate
        });
    }

    return forwards;
}
export function solveParCurveNewtonRaphson(
    pillars: Pillar[],
    options?: {
        tolerance?: number;
        maxIterations?: number;
        initialGuess?: number;
    }
): CurvePoint[] {
    const tolerance = options?.tolerance ?? 1e-12;
    const maxIterations = options?.maxIterations ?? 100;

    const sorted = [...pillars].sort((a, b) => a.t - b.t);
    const curve: CurvePoint[] = [];

    for (const { t, parRate } of sorted) {
        const knownDiscountSum = curve.filter((p) => p.t < t).reduce((sum, p) => sum + p.discount, 0); // Default initial guess:
        // use explicit override if provided,
        // otherwise use the current pillar par rate

        let z = options?.initialGuess ?? parRate;

        for (let iteration = 0; iteration < maxIterations; iteration++) {
            const discount = 1 / Math.pow(1 + z, t);

            const lhs = parRate * (knownDiscountSum + discount);

            const rhs = 1 - discount;

            const f = lhs - rhs; // d/dz [(1 + z)^(-t)]

            const discountDerivative = -t / Math.pow(1 + z, t + 1);

            const fDerivative = (parRate + 1) * discountDerivative;

            if (Math.abs(fDerivative) < 1e-18) {
                throw new Error(`Derivative too small at t=${t}`);
            }

            const nextZ = z - f / fDerivative;

            if (Math.abs(nextZ - z) < tolerance) {
                z = nextZ;
                break;
            }

            z = nextZ;

            if (iteration === maxIterations - 1) {
                throw new Error(`Newton-Raphson did not converge at t=${t}`);
            }
        }

        const discount = 1 / Math.pow(1 + z, t);

        curve.push({
            t,
            discount,
            zeroRate: z
        });
    }

    return curve;
}

export function createRegulatoryDiscountCurve(
    forwardPoints: ForwardRatePoint[],
    options?: {
        ufr?: number;
        maxT?: number;
    }
): CurvePoint[] {
    const ufr = options?.ufr ?? 0.033;
    const maxT = options?.maxT ?? 100;

    const forwardByToT = new Map(forwardPoints.map((p) => [p.toT, p.forwardRate]));

    const curve: CurvePoint[] = [];

    for (let t = 1; t <= maxT; t++) {
        const forwardRate = forwardByToT.get(t) ?? ufr;

        const previousZeroRate = t === 1 ? 0 : curve[t - 2].zeroRate;

        const zeroRate = Math.pow((1 + forwardRate) * Math.pow(1 + previousZeroRate, t - 1), 1 / t) - 1;

        const discount = 1 / Math.pow(1 + zeroRate, t);

        curve.push({
            t,
            zeroRate,
            discount
        });
    }

    return curve;
}

export function bootstrapDiscountCurveWithUfr(
    pillars: Pillar[],
    options?: {
        ufr?: number;
        maxT?: number;
        modifier?: number;
    }
): CurvePoint[] {
    const ufr = options?.ufr ?? 0.033;
    const maxT = options?.maxT ?? 100;
    const modifier = options?.modifier ?? -0.0015;

    const adjustedPillars = pillars.map((p) => ({
        ...p,
        parRate: p.parRate + modifier
    }));

    const curve = solveParCurveNewtonRaphson(adjustedPillars);

    const forwards = calculateForwardRates(curve);

    const forwardsWithUfr = applyUfrWeighting(forwards, ufr);

    const regulatoryCurve = createRegulatoryDiscountCurve(forwardsWithUfr, { maxT });

    console.log(regulatoryCurve);
    return regulatoryCurve;
}

// use this later
function _interpolateDiscount(curve: CurvePoint[], t: number): number {
    const exact = curve.find((point) => Math.abs(point.t - t) < 1e-10);
    if (exact) {
        return exact.discount;
    }

    const before = [...curve].reverse().find((point) => point.t < t);
    const after = curve.find((point) => point.t > t);

    if (!before || !after) {
        throw new Error(`Cannot interpolate discount factor at t=${t}`);
    }

    const w = (t - before.t) / (after.t - before.t);
    const z = before.zeroRate + w * (after.zeroRate - before.zeroRate);

    return Math.exp(-z * t);
}
